import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Icon,
} from "@mui/material";
import { useContext, useState, useEffect, useMemo, useRef } from "react";
import { TbInfoCircle } from "react-icons/tb";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DashboardFiltersDto, PeriodType } from "../../types/dashboard";
import { DashboardFilters } from "./Components/DashboardFilters";
import { CEOFilters } from "./Components/CEOFilters";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import {
  DashboardProvider,
  DashboardContextRefType,
} from "../../contexts/DashboardContext";
import { AuthContext } from "../../contexts/auth";
import { SupervisorDashboard } from "./Dashboards/SupervisorDashboard";
import { CEODashboard } from "./Dashboards/CEODashboard";
import { ManagerDashboard } from "./Dashboards/ManagerDashboard";
import { useHistory } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { useAllCells, useProfile } from "../../queries/hooks";

// Tipos de dashboard disponíveis
type DashboardType = "Supervisor" | "Gerente" | "CEO";

// Ordem fixa dos dashboards para exibição no select
const DASHBOARD_ORDER: DashboardType[] = ["Gerente", "Supervisor", "CEO"];

// Interface para submódulos de permissão
interface ModulePermission {
  tmp?: boolean;
  active?: boolean;
}

interface SubmoduleType {
  create?: ModulePermission;
  delete?: ModulePermission;
  update?: ModulePermission;
  view?: ModulePermission;
}

interface Submodule {
  name: string;
  type?: SubmoduleType;
}

export const Dashboard = () => {
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    return { start, end };
  };

  const getNext14DaysRange = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + 14);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  const [showBanner, setShowBanner] = useState(true);
  const history = useHistory();
  const { data: userData } = useProfile();
  const { data: dataCell } = useAllCells();

  const filteredCells = dataCell
    ?.filter(
      (cell: any) =>
        cell?.coordinators_user_ids?.includes(userData?._id) ||
        cell?.leader_user_ids?.includes(userData?._id) ||
        cell?.cell_admins_ids?.includes(userData?._id)
    )
    ?.map((i: any) => i?._id);

  const { accessOptions } = useContext(AuthContext);
  const [dashboardType, setDashboardType] =
    useState<DashboardType>("Supervisor");
  const [commonFilters, setCommonFilters] = useState<DashboardFiltersDto>(
    () => {
      const { start, end } = getCurrentMonthRange();

      return {
        startDate: { start, end },
      };
    }
  );
  const [ceoFilters, setCEOFilters] = useState<DashboardFiltersDto>(() => {
    const { start, end } = getNext14DaysRange();

    return {
      includeExtraFields: true,
      startDate: { start, end },
    };
  });

  // Referência para acessar funções do DashboardContext
  const dashboardContextRef = useRef<DashboardContextRefType>(null);

  // Obter as permissões de visualização para os diferentes dashboards
  const availableDashboards = useMemo(() => {
    if (!accessOptions || !Array.isArray(accessOptions)) return [];

    const dashboardModule = accessOptions.find(
      (option) => option.name === "Dashboard"
    );
    if (!dashboardModule || !dashboardModule.submodules) return [];

    const allowedDashboards = dashboardModule.submodules
      .filter(
        (submodule: Submodule) =>
          submodule.type?.view?.active &&
          ["Visão CEO", "Visão Supervisores", "Visão Gerentes"].includes(
            submodule.name
          )
      )
      .map((submodule: Submodule) => {
        if (submodule.name === "Visão Supervisores") return "Supervisor";
        if (submodule.name === "Visão Gerentes") return "Gerente";
        if (submodule.name === "Visão CEO") return "CEO";
        return null;
      })
      .filter(Boolean) as DashboardType[];

    // Ordenar os dashboards de acordo com a ordem fixa
    return DASHBOARD_ORDER.filter((dashboard) =>
      allowedDashboards.includes(dashboard)
    );
  }, [accessOptions]);

  // Definir o dashboard padrão quando as permissões são carregadas
  useEffect(() => {
    if (availableDashboards.length > 0) {
      setDashboardType(availableDashboards[0]);
    }
  }, [availableDashboards]);

  // Efeito para atualizar o DashboardContext quando o tipo de dashboard muda
  // NOTA: commonFilters e ceoFilters NÃO são dependências — o clique manual em
  // "Aplicar Filtros" já chama applyFilters diretamente via DashboardContextRef.
  useEffect(() => {
    if (!dashboardContextRef.current) return;
    if (dashboardType === "CEO") {
      dashboardContextRef.current.applyFilters(ceoFilters);
    } else {
      dashboardContextRef.current.applyFilters(commonFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardType]);

  const handleFiltersChange = (filters: DashboardFiltersDto) => {
    // Verificar se há algum filtro com valor
    const hasFilters = Object.entries(filters).some(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.length > 0) return true;
      if (typeof value === "boolean") return true;
      if (typeof value === "number") return true;
      if (Array.isArray(value) && value.length > 0) return true;
      if (value && typeof value === "object") {
        if ("start" in value && value.start) return true;
        if ("end" in value && value.end) return true;
      }
      return false;
    });

    // Só atualizar os filtros se houver algum valor
    setCommonFilters(hasFilters ? filters : {});
    // Dispara applyFilters diretamente para evitar dupla-chamada do useEffect
    if (dashboardContextRef.current) {
      dashboardContextRef.current.applyFilters(hasFilters ? filters : {});
    }
  };

  const handleCEOFiltersChange = (filters: DashboardFiltersDto) => {
    const newFilters = {
      ...filters,
      includeExtraFields: true,
    };

    setCEOFilters(newFilters);
    // Dispara applyFilters diretamente para evitar dupla-chamada do useEffect
    if (dashboardContextRef.current) {
      dashboardContextRef.current.applyFilters(newFilters);
    }
  };

  const handleDashboardTypeChange = (event: SelectChangeEvent<string>) => {
    const newDashboardType = event.target.value as DashboardType;
    setDashboardType(newDashboardType);

    // Se o novo tipo for CEO, garantir que temos os filtros corretos
    if (newDashboardType === "CEO") {
      const currentCEOFilters = {
        ...ceoFilters,
        includeExtraFields: true,
      };

      setCEOFilters(currentCEOFilters);
    }
  };

  // Renderizar o dashboard adequado com base na seleção
  const renderDashboard = () => {
    switch (dashboardType) {
      case "CEO":
        return <CEODashboard filters={ceoFilters} />;
      case "Gerente":
        return <ManagerDashboard filters={commonFilters} />;
      case "Supervisor":
      default:
        return (
          <SupervisorDashboard
            filters={commonFilters}
            allowedCellIds={filteredCells}
          />
        );
    }
  };

  // Renderizar o componente de filtros adequado com base no tipo de dashboard
  const renderFilters = () => {
    if (dashboardType === "CEO") {
      return (
        <CEOFilters
          filters={ceoFilters}
          onFiltersChange={handleCEOFiltersChange}
        />
      );
    } else {
      return (
        <DashboardFilters
          filters={commonFilters}
          onFiltersChange={handleFiltersChange}
        />
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <DashboardProvider
        selectedDashboardType={dashboardType}
        ref={dashboardContextRef}
      >
        <Grid container sx={{ backgroundColor: "#F9FCFF", overflow: "auto" }}>
          <Grid
            item
            xs={12}
            display={"flex"}
            flexDirection={"column"}
            sx={{ pr: 3, pl: 2, py: 4 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 400, fontSize: 24 }}>
                Dashboard
              </Typography>

              <Button
                variant="contained"
                onClick={() => history.push(`/projeto`)}
                startIcon={<FaPlus size={12} />}
              >
                Novo Projeto
              </Button>
            </Box>

            {showBanner && (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={0.75}
                mb={2}
                px={2}
                py={1.5}
                bgcolor="#EDF4FE"
                sx={{
                  boxShadow: "0px 4px 18px 0px #4B465C1A",
                  borderRadius: "10px",
                  border: "1px solid #1976D2",
                }}
              >
                <TbInfoCircle
                  size={16}
                  color="#1976D2"
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  color="#1976D2"
                  sx={{ fontWeight: 700, flex: 1 }}
                >
                  {dashboardType === "CEO"
                    ? "Exibindo MMs com início nos próximos 14 dias. Para consultar outros períodos, ajuste o filtro de início ou finalização do evento."
                    : "Exibindo MMs com início neste mês. Para consultar outros períodos, ajuste o filtro de início ou finalização do evento."}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowBanner(false)}
                  sx={{ color: "#1976D2", ml: 0.5, p: 0.25 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Card sx={{ display: "flex" }}>
              {renderFilters()}

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderLeft: "1px solid #E5E7EB",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, fontSize: 22 }}
                  >
                    Controle de eventos
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* Botão de debug oculto — reativar apenas para diagnóstico */}
                    {/* <DebugDownloadButton /> */}

                    {availableDashboards.length > 1 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, fontSize: 14 }}
                        >
                          Ver como:
                        </Typography>

                        <FormControl sx={{ width: 200 }} size="small">
                          <Select
                            value={dashboardType}
                            onChange={handleDashboardTypeChange}
                            displayEmpty
                          >
                            {availableDashboards.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>

                {renderDashboard()}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </DashboardProvider>
    </LocalizationProvider>
  );
};
